import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mic } from "lucide-react";
import { Music, Flame, Target, Sparkles, Compass, Bell, LogOut, ChevronRight, AlertCircle } from "lucide-react";
import { childApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ChoirContent from "../components/ChoirContent";

const MASTERY_COLOURS = {
  "Not Started": "bg-[#E5DEC9]",
  Learning: "bg-[#B8860B]",
  Practising: "bg-[#C69B3D]",
  Secure: "bg-[#2D6A4F]",
  Mastered: "bg-[#7A1C2E]",
};

export default function ChildHome() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [diag, setDiag] = useState(null);
  const { session, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    childApi.get("/child/home").then((r) => setData(r.data)).catch((e) => setError(e?.response?.data?.detail || "Could not load"));
    childApi.get("/child/diagnostic/status").then((r) => setDiag(r.data)).catch(() => {});
  }, []);

  const startDaily = async () => {
    try {
      const { data: d } = await childApi.post("/child/daily/start");
      navigate("/play/session", { state: { sessionData: d } });
    } catch (e) {
      if (e?.response?.status === 409) {
        setData((prev) => ({ ...prev, daily_done: true }));
      }
    }
  };

  if (error) return <div className="min-h-screen bg-[#FAF7EE] p-8 text-[#A62B2B]" data-testid="home-error">{error}</div>;
  if (!data) return (
    <div className="min-h-screen bg-[#FAF7EE] flex items-center justify-center" data-testid="home-loading">
      <Music className="w-8 h-8 text-[#C69B3D] animate-pulse" />
    </div>
  );

  const name = data.display_name;
  const quest = data.active_quest;

  return (
    <div className="min-h-screen bg-[#FAF7EE]" data-testid="chorister-home">
      <header className="bg-[#0B192C] text-[#FAF7EE] px-5 pt-5 pb-16 rounded-b-[2rem]">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Music className="w-5 h-5 text-[#C69B3D]" aria-hidden />
              <span className="font-display font-bold">ChoirQuest</span>
            </div>
            <div className="flex items-center gap-1">
              <Link to="/play/notices" aria-label="Notifications" data-testid="notifications-btn"
                className="relative p-3 rounded-full hover:bg-white/10 min-h-[48px] min-w-[48px] flex items-center justify-center">
                <Bell className="w-5 h-5" />
                {data.unread_notifications > 0 && (
                  <span data-testid="notifications-badge" className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-[#7A1C2E] text-[10px] font-bold flex items-center justify-center">{data.unread_notifications}</span>
                )}
              </Link>
              <button onClick={() => { logout(); navigate("/play"); }} data-testid="finish-session-btn"
                className="flex items-center gap-1.5 px-4 py-3 rounded-full bg-[#7A1C2E] hover:bg-[#8F2438] text-sm font-bold min-h-[48px]">
                <LogOut className="w-4 h-4" /> Finish Session
              </button>
            </div>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold mt-5" data-testid="welcome-heading">Welcome back, {name}</h1>
          <p className="text-sm text-[#FAF7EE]/60">{data.institution_name} · Grade {data.grade}</p>
          <div className="flex flex-wrap gap-3 mt-4">
            <span data-testid="notes-total" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C69B3D] text-[#0B192C] font-bold">
              <Music className="w-4 h-4" aria-hidden /> {data.notes_total.toLocaleString()} Notes
            </span>
            {data.settings?.streaks !== false && (
              <span data-testid="streak-count" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#12253E] border border-[#1E3A5F] font-semibold">
                <Flame className="w-4 h-4 text-[#C69B3D]" aria-hidden /> {data.streak?.count || 0}-day streak
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 -mt-8 pb-16 space-y-5">
        <ChoirContent />
        {quest ? (
          <section className="rounded-2xl bg-white border-2 border-[#C69B3D]/40 shadow-sm p-5 sm:p-6 gold-glow" data-testid="active-quest-card">
            <div className="flex items-center gap-2 text-[#7A1C2E]">
              <Target className="w-4 h-4" aria-hidden />
              <span className="text-xs font-bold uppercase tracking-widest">Your Quest</span>
            </div>
            <h2 className="font-display text-xl font-bold text-[#0B192C] mt-2">{quest.title}</h2>
            <p className="text-sm text-[#566270] mt-1">{quest.description}</p>
            <div className="mt-4">
              <div className="flex justify-between text-xs font-semibold text-[#566270]">
                <span data-testid="quest-progress-label">{Math.min(quest.current, quest.target)} / {quest.target}</span>
                <span>Reward: {quest.reward_notes} Notes</span>
              </div>
              <div className="h-3 rounded-full bg-[#F3EFE0] mt-1.5 overflow-hidden">
                <div data-testid="quest-progress-bar" className="h-full rounded-full bg-[#C69B3D] transition-all"
                  style={{ width: `${Math.min(100, (quest.current / Math.max(1, quest.target)) * 100)}%` }} />
              </div>
            </div>
            <Link to="/play/quests" data-testid="continue-quest-cta"
              className="mt-4 inline-flex items-center gap-1.5 px-5 py-3 rounded-xl bg-[#0B192C] text-[#FAF7EE] font-bold text-sm hover:bg-[#12253E] transition-colors min-h-[48px]">
              View my Quests <ChevronRight className="w-4 h-4" />
            </Link>
          </section>
        ) : (
          <section className="rounded-2xl bg-white border border-[#E5DEC9] p-5" data-testid="no-quest-card">
            <p className="text-sm text-[#566270]">No active Quests right now — your choir team will set one soon.</p>
          </section>
        )}

        {diag && !diag.has_baseline && (
          <Link to="/play/diagnostic" data-testid="diagnostic-card"
            className="block rounded-2xl bg-white border-2 border-[#C69B3D]/40 p-5 sm:p-6 hover:border-[#C69B3D] transition-colors">
            <div className="flex items-center gap-2 text-[#8a6a10]">
              <Compass className="w-4 h-4" aria-hidden />
              <span className="text-xs font-bold uppercase tracking-widest">New here?</span>
            </div>
            <h2 className="font-display text-xl font-bold text-[#0B192C] mt-2">Find My Starting Point</h2>
            <p className="text-sm text-[#566270] mt-1">
              {diag.in_progress
                ? "You're part-way through — tap to carry on."
                : "A few short musical puzzles so your choir leader knows the best place for you to begin."}
            </p>
            <span className="mt-4 inline-flex items-center gap-1.5 px-5 py-3 rounded-xl bg-[#0B192C] text-[#FAF7EE] font-bold text-sm min-h-[48px]">
              {diag.in_progress ? "Continue" : "Start"} <ChevronRight className="w-4 h-4" />
            </span>
          </Link>
        )}
        {diag?.has_baseline && (
          <Link to="/play/diagnostic" data-testid="progress-check-card"
            className="block rounded-2xl bg-white border border-[#E5DEC9] p-5 hover:border-[#C69B3D]/60 transition-colors">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="font-display text-base font-bold text-[#0B192C]">Progress Check</h2>
                <p className="text-xs text-[#566270] mt-0.5">Show how far you've come since your starting point.</p>
              </div>
              <ChevronRight className="w-5 h-5 text-[#C69B3D] shrink-0" />
            </div>
          </Link>
        )}

        <Link to="/play/pitch-trainer" data-testid="pitch-trainer-card"
          className="block rounded-2xl bg-white border border-[#E5DEC9] p-5 hover:border-[#C69B3D]/60 transition-colors">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-base font-bold text-[#0B192C]">Pitch Trainer</h2>
              <p className="text-xs text-[#566270] mt-0.5">Tune your voice like an instrument — sing a note and watch the needle.</p>
            </div>
            <Mic className="w-5 h-5 text-[#C69B3D] shrink-0" aria-hidden />
          </div>
        </Link>

        <div className="grid gap-5 sm:grid-cols-2">
          <section className="rounded-2xl bg-[#0B192C] text-[#FAF7EE] p-5 sm:p-6" data-testid="daily-notes-card">
            <div className="flex items-center gap-2 text-[#C69B3D]">
              <Sparkles className="w-4 h-4" aria-hidden />
              <span className="text-xs font-bold uppercase tracking-widest">Daily Notes</span>
            </div>
            <p className="font-display text-lg font-bold mt-2">5 quick questions</p>
            <p className="text-sm text-[#FAF7EE]/60">About two minutes. +25 Notes.</p>
            {data.daily_done ? (
              <p className="mt-4 inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-[#2D6A4F]/30 border border-[#2D6A4F] text-sm font-semibold" data-testid="daily-done-label">
                Daily Notes complete — nicely done!
              </p>
            ) : (
              <button onClick={startDaily} data-testid="start-daily-btn"
                className="mt-4 w-full px-5 py-3 rounded-xl bg-[#C69B3D] text-[#0B192C] font-bold hover:bg-[#D8AB49] transition-colors min-h-[48px]">
                Start Daily Notes
              </button>
            )}
          </section>

          <section className="rounded-2xl bg-white border border-[#E5DEC9] p-5 sm:p-6" data-testid="weak-spots-card">
            <div className="flex items-center gap-2 text-[#7A1C2E]">
              <AlertCircle className="w-4 h-4" aria-hidden />
              <span className="text-xs font-bold uppercase tracking-widest">Worth another look</span>
            </div>
            {data.weak_spots.length ? (
              <ul className="mt-3 space-y-2">
                {data.weak_spots.map((w) => (
                  <li key={w.topic_id}>
                    <button onClick={async () => {
                        const { data: d } = await childApi.post("/child/challenge/start", { topic_id: w.topic_id, kind: "challenge" });
                        navigate("/play/session", { state: { sessionData: d } });
                      }} data-testid={`weak-spot-${w.topic_id}`}
                      className="w-full flex items-center justify-between rounded-xl bg-[#FBF0F2] px-4 py-3 hover:bg-[#f7e2e7] transition-colors min-h-[48px]">
                      <span className="text-sm font-semibold text-[#0B192C]">{w.topic}</span>
                      <span className="text-xs font-bold text-[#7A1C2E]">{w.accuracy}%</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-[#566270] mt-3">No weak spots spotted yet — keep practising!</p>
            )}
          </section>
        </div>

        <section data-testid="keep-going-section">
          <div className="flex items-center gap-2 text-[#0B192C] mb-3">
            <Compass className="w-4 h-4 text-[#C69B3D]" aria-hidden />
            <h2 className="text-xs font-bold uppercase tracking-widest">Keep going</h2>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {data.modules.map((m) => (
              <Link key={m.id} to={`/play/module/${m.id}`} data-testid={`module-card-${m.id}`}
                className="rounded-2xl bg-white border border-[#E5DEC9] p-5 hover:border-[#C69B3D] hover:shadow-md transition-all group">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display font-bold text-[#0B192C] group-hover:text-[#7A1C2E] transition-colors">{m.name}</h3>
                  {m.liturgical && <span className="text-[10px] font-bold uppercase tracking-wider text-[#7A1C2E] bg-[#FBF0F2] px-2 py-0.5 rounded-full">Choral</span>}
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex-1 h-2.5 rounded-full bg-[#F3EFE0] overflow-hidden">
                    <div className="h-full rounded-full bg-[#2D6A4F] transition-all" style={{ width: `${m.mastery_pct}%` }} />
                  </div>
                  <span className="text-xs font-bold text-[#566270] w-10 text-right">{m.mastery_pct}%</span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/play/quests" data-testid="view-quests-link"
            className="flex-1 text-center px-5 py-4 rounded-2xl border-2 border-[#0B192C] text-[#0B192C] font-bold hover:bg-[#0B192C] hover:text-[#FAF7EE] transition-colors min-h-[52px]">
            My Quests
          </Link>
          <Link to="/play/badges" data-testid="view-badges-link"
            className="flex-1 text-center px-5 py-4 rounded-2xl border-2 border-[#C69B3D] text-[#0B192C] font-bold hover:bg-[#F6EED8] transition-colors min-h-[52px]">
            My Achievements
          </Link>
        </div>

        {data.disclaimer && (
          <p className="text-[11px] text-[#566270]/80 text-center pt-4" data-testid="disclaimer-text">{data.disclaimer}</p>
        )}
      </main>
    </div>
  );
}
